import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, Dialog, DialogContent, Typography } from '@mui/material';
import dayjs from 'dayjs';
import axios from 'axios';
import { useToast } from '../../../contexts/ToastContext';
import { displayDateTimeToUTC } from '../../../utils/timezoneUtils';
import MaintenanceFormDialog from './MaintenanceFormDialog';

const EMPTY_FORM = {
  aircraftId: '',
  type: 'REPAIR',
  title: '',
  description: '',
  priority: 'MEDIUM',
  status: 'SCHEDULED',
  scheduledDate: null,
  scheduleMode: 'FULL_DAY',
  scheduledEndDate: null,
  inspectionType: '',
  ampScheduleId: '',
  ampChecks: [],
  creationSourceRemarks: '',
  linkedIntimationIds: [],
  workOrderCsvFile: null
};

/**
 * CAMO dialog to create a regular work order from an EDR inbox row (prefilled from EDR + parent WO).
 */
const RaiseEdrWorkOrderDialog = ({ open, onClose, edrRow, aircraft, displayTimezone, onCreated }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [edrContext, setEdrContext] = useState(null);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetState = useCallback(() => {
    setFormData(EMPTY_FORM);
    setEdrContext(null);
    setLoadingPrefill(false);
    setSubmitting(false);
  }, []);

  const loadPrefill = useCallback(async () => {
    if (!edrRow?.workOrderId || edrRow.edrIndex == null) return;
    setLoadingPrefill(true);
    setEdrContext(null);
    try {
      const res = await axios.get(`/api/maintenance/${edrRow.workOrderId}/edr/raise-work-order/prefill`, {
        params: { edrIndex: edrRow.edrIndex }
      });
      const data = res.data || {};
      const defaults = data.defaults || {};
      setEdrContext({
        parentWorkOrderNumber: data.parentWorkOrderNumber,
        parentTitle: data.parentTitle,
        suggestedWorkOrderNumber: data.suggestedWorkOrderNumber,
        edrSummary: data.edrSummary,
        parentAmpChecks: Array.isArray(data.parentAmpChecks) ? data.parentAmpChecks : [],
        parentAmpScheduleId: data.parentAmpScheduleId || defaults.ampScheduleId || null
      });
      setFormData({
        aircraftId: defaults.aircraftId || '',
        type: defaults.type || 'REPAIR',
        title: defaults.title || '',
        description: defaults.description || '',
        priority: defaults.priority || 'MEDIUM',
        status: 'SCHEDULED',
        scheduledDate: defaults.scheduledDate ? dayjs(defaults.scheduledDate) : null,
        scheduleMode: defaults.scheduleMode === 'HOURLY' ? 'HOURLY' : 'FULL_DAY',
        scheduledEndDate: defaults.scheduledEndDate ? dayjs(defaults.scheduledEndDate) : null,
        inspectionType: defaults.inspectionType || '',
        ampScheduleId: defaults.ampScheduleId || '',
        ampChecks: Array.isArray(defaults.ampChecks) ? defaults.ampChecks : [],
        creationSourceRemarks: defaults.creationSourceRemarks || '',
        linkedIntimationIds: [],
        workOrderCsvFile: null
      });
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to load EDR work order details', 'error');
      onClose();
    } finally {
      setLoadingPrefill(false);
    }
  }, [edrRow, onClose, showToast]);

  useEffect(() => {
    if (open && edrRow) {
      loadPrefill();
    } else if (!open) {
      resetState();
    }
  }, [open, edrRow, loadPrefill, resetState]);

  const handleSubmit = async () => {
    if (!edrRow || edrRow.edrIndex == null) return;
    if (!formData.aircraftId?.toString().trim()) {
      showToast('Please select an aircraft.', 'warning');
      return;
    }
    if (!formData.title?.trim()) {
      showToast('Please enter a title for the work order.', 'warning');
      return;
    }
    if (!String(formData.creationSourceRemarks || '').trim()) {
      showToast('Creation source / reason is required.', 'warning');
      return;
    }
    const checklist = Array.isArray(formData.ampChecks)
      ? formData.ampChecks.filter((c) => String(c?.description || '').trim())
      : [];
    if (!formData.ampScheduleId) {
      showToast('Select an AMP schedule for this EDR work order.', 'warning');
      return;
    }
    if (checklist.length === 0) {
      const hasParentChecks = Array.isArray(edrContext?.parentAmpChecks) && edrContext.parentAmpChecks.length > 0;
      showToast(
        hasParentChecks
          ? 'Select at least one check that failed on the parent work order.'
          : 'Map at least one AMP check to this EDR work order.',
        'warning'
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        edrIndex: edrRow.edrIndex,
        aircraftId: formData.aircraftId,
        type: formData.type,
        title: formData.title.trim(),
        description: formData.description || '',
        priority: formData.priority,
        creationSourceRemarks: String(formData.creationSourceRemarks).trim()
      };

      if (formData.scheduledDate != null) {
        const d = dayjs(formData.scheduledDate);
        const mode = formData.scheduleMode === 'HOURLY' ? 'HOURLY' : 'FULL_DAY';
        payload.scheduleMode = mode;
        if (d.isValid()) {
          if (mode === 'HOURLY') {
            payload.scheduledDate = displayTimezone
              ? (displayDateTimeToUTC(d.format('YYYY-MM-DD'), d.format('HH:mm'), displayTimezone) ?? d.toISOString())
              : d.toISOString();
            const end = formData.scheduledEndDate != null ? dayjs(formData.scheduledEndDate) : null;
            if (end && end.isValid()) {
              payload.scheduledEndDate = displayTimezone
                ? (displayDateTimeToUTC(end.format('YYYY-MM-DD'), end.format('HH:mm'), displayTimezone) ?? end.toISOString())
                : end.toISOString();
            }
          } else {
            payload.scheduledDate = displayTimezone
              ? (displayDateTimeToUTC(d.format('YYYY-MM-DD'), '00:00', displayTimezone) ?? d.toISOString())
              : d.toISOString();
            payload.scheduledEndDate = null;
          }
        }
      }

      if (formData.inspectionType) {
        payload.inspectionType = formData.inspectionType;
      }
      if (formData.ampScheduleId) {
        payload.ampScheduleId = formData.ampScheduleId;
        if (formData.maintenanceType) {
          payload.maintenanceType = formData.maintenanceType;
        }
      }
      payload.ampChecks = checklist;

      const res = await axios.post(`/api/maintenance/${edrRow.workOrderId}/edr/raise-work-order`, payload);
      const wo = res.data?.workOrder;
      showToast(res.data?.message || `Work order ${wo?.workOrderNumber || ''} created`, 'success');
      if (onCreated && wo) {
        onCreated(wo);
      }
      onClose();
    } catch (e) {
      const data = e.response?.data;
      let message = data?.message;
      if (!message && Array.isArray(data?.errors) && data.errors.length > 0) {
        message = data.errors.map((err) => err.msg).join('; ');
      }
      showToast(message || 'Failed to create work order from EDR', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  if (loadingPrefill || !edrContext) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Loading work order details from EDR…
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <MaintenanceFormDialog
      open={open}
      onClose={onClose}
      formData={formData}
      setFormData={setFormData}
      aircraft={aircraft}
      selectedMaintenance={null}
      onSubmit={submitting ? () => {} : handleSubmit}
      displayTimezone={displayTimezone}
      edrRaiseMode
      edrContext={edrContext}
      lockAircraft
      submitLabel={submitting ? 'Creating…' : 'Create work order'}
    />
  );
};

export default RaiseEdrWorkOrderDialog;
